import { WorkspaceDailySource } from './WorkspaceDailySource.jsx'
import PriceAlertPanel from '../priceAlerts/PriceAlertPanel.jsx'
import PriceAlertDialog from '../priceAlerts/PriceAlertDialog.jsx'
import { usePriceAlerts } from '../priceAlerts/PriceAlertProvider.jsx'
import { useSharedTimeframeProfiles } from '../../hooks/useSharedTimeframeProfiles.js'
import { useChartRestoreCommand } from './useChartRestoreCommand.js'
import { normalizeChartMarginsByTimeframe } from '../../lib/chart/chartMargins.js'
import { normalizeRsAppearance } from './relativeStrength/lineAppearance.js'
import { useCommandHandlers } from '../../commands/CommandHandlers.jsx'
import { useOptionalCommands } from '../../commands/CommandContext.js'
import { WORKSPACE_TOOLBAR_ITEMS, CHART_LAYOUT_COMMANDS, CHART_PANE_COMMANDS } from '../../commands/workspaceToolbarItems.js'
import { DailyPivotSourceProvider } from '../../hooks/useDailyPivotSource.js'
import SharedAnchoredVwap from './SharedAnchoredVwap.jsx'
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ChartTopBar from './ChartTopBar.jsx'
import ChartLayoutMenu from './ChartLayoutMenu.jsx'
import ChartIntervalCommandSurface from './ChartIntervalCommandSurface.jsx'
import TradeChart from './TradeChart.jsx'
import WorkspacePaneToolbar from './WorkspacePaneToolbar.jsx'
import RelativeStrengthCharts from './relativeStrength/RelativeStrengthCharts.jsx'
import { readServerChartPreference, writeServerChartPreference } from '../../lib/chart/chartPreferenceStore.js'

const TIMEFRAMES = new Set(['1min', '2min', '5min', '15min', '30min', '65min', 'daily', 'weekly'])
const EXTENDED = new Set(['1min', '2min', '5min', '15min', '30min'])
const SLOTS = ['top', 'lower', 'lowerRight', 'upperLeft']
const LAYOUT_SLOTS = {
  single: ['top'], split: ['top', 'lower'],
  triple: ['top', 'lower', 'lowerRight'], tripleTop: ['upperLeft', 'top', 'lower'], quad: SLOTS,
}
const PANE_ORDER = {
  ...LAYOUT_SLOTS,
  quad: ['upperLeft', 'top', 'lower', 'lowerRight'],
}
const clampRatio = (value) => Math.max(0.2, Math.min(0.8, value))
const normalizeRatio = (value) => Number.isFinite(value) ? clampRatio(value) : 0.5
const legacyPreferenceKey = 'workspace-layout'
const preferenceKey = 'workspace-layout-shared-timeframes-production-v1'
const CHART_TYPES = new Set(['bar', 'candlestick'])
const currentTheme = () => document.documentElement.classList.contains('dark') ? 'dark' : 'light'
function normalizeChartTypes(value) {
  return Object.fromEntries(['light', 'dark'].map(theme => [theme,
    Object.fromEntries(SLOTS.filter(slot => CHART_TYPES.has(value?.[theme]?.[slot]))
      .map(slot => [slot, value[theme][slot]])),
  ]))
}
function normalizeProfile(value, top) {
  const timeframe = TIMEFRAMES.has(value?.timeframe) ? value.timeframe : '30min'
  return {
    timeframe,
    chartType: ['bar', 'candlestick'].includes(value?.chartType) ? value.chartType : top.chartType,
    marketSession: value?.marketSession === 'all' && EXTENDED.has(timeframe) ? 'all' : 'regular',
    positionOverlayEnabled: value?.positionOverlayEnabled ?? top.positionOverlayEnabled,
  }
}
function normalize(value, top) {
  return {
    alertsVisible: Object.fromEntries(SLOTS.map(slot => [slot, value?.alertsVisible?.[slot] !== false])),
    version: 1,
    topTimeframe: TIMEFRAMES.has(value?.topTimeframe) ? value.topTimeframe : top.timeframe,
    rsTimeframe: TIMEFRAMES.has(value?.rsTimeframe) ? value.rsTimeframe : 'daily',
    rsMarketSession: EXTENDED.has(value?.rsTimeframe) && value?.rsMarketSession === 'all' ? 'all' : 'regular',
    rsMargins: normalizeChartMarginsByTimeframe(value?.rsMargins),
    rsAppearance: normalizeRsAppearance(value?.rsAppearance, value?.rsColors),
    rsColors: Object.fromEntries(['asset', 'benchmark'].filter(role => /^#[0-9a-f]{6}$/i.test(value?.rsColors?.[role] || '')).map(role => [role, value.rsColors[role]])),
    paneType: value?.paneType === 'relative-strength' ? 'relative-strength' : 'chart',
    rsBenchmark: /^[A-Z0-9][A-Z0-9.!^-]{0,14}$/.test(value?.rsBenchmark || '') ? value.rsBenchmark : 'SPY',
    layout: Object.hasOwn(LAYOUT_SLOTS, value?.layout) ? value.layout : 'single',
    ratio: normalizeRatio(value?.ratio),
    topRatio: normalizeRatio(value?.topRatio),
    bottomRatio: normalizeRatio(value?.bottomRatio),
    syncCrosshair: value?.syncCrosshair === true,
    topPositionOverlayEnabled: typeof value?.topPositionOverlayEnabled === 'boolean' ? value.topPositionOverlayEnabled : undefined,
    chartTypesByTheme: normalizeChartTypes(value?.chartTypesByTheme),
    ...Object.fromEntries(SLOTS.slice(1).map(slot => [slot, normalizeProfile(value?.[slot], top)])),
  }
}

// Stable slots preserve the original main/lower charts when geometry changes.
// The parent still owns the main chart controls and workspace symbol navigation.
export default function WorkspaceCharts({ userId, toolbarProps, chartProps, getOverlayProps, onOpenSymbol }) {
  const shared = useSharedTimeframeProfiles(chartProps.mode === 'live' ? userId : null)
  const commandContext = useOptionalCommands()
  const [preferences, setPreferences] = useState(() => normalize(null, toolbarProps))
  const preferencesRef = useRef(preferences)
  const [theme, setTheme] = useState(currentTheme)
  useEffect(() => {
    const syncTheme = () => setTheme(currentTheme())
    syncTheme()
    const observer = new MutationObserver(syncTheme)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  const [hydrated, setHydrated] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const saveQueue = useRef(Promise.resolve())
  const saveRevision = useRef(0)
  const [active, setActive] = useState('top')
  // Pane choice and benchmark use the existing server-backed workspace layout.
  const [paneType, setPaneType] = useState('chart')
  const [drawingTool, setDrawingTool] = useState('select')
  const rsRef = useRef(null)
  const [rsActiveRole, setRsActiveRole] = useState('asset')
  const [maximized, setMaximized] = useState(null)
  useChartRestoreCommand({ ready: hydrated, onRestore: () => setMaximized(null) })
  const [mountedSlots, setMountedSlots] = useState(['top'])
  const chartRefs = useMemo(() => Object.fromEntries(SLOTS.map(slot => [slot, createRef()])), [])
  const containerRef = useRef(null)
  const paneElements = useRef({})
  const [paneFocusRequest, setPaneFocusRequest] = useState(null)
  useEffect(() => {
    if (paneFocusRequest) paneElements.current[paneFocusRequest.slot]?.focus({ preventScroll: true })
  }, [paneFocusRequest])
  const dragRef = useRef(null)
  const previousSyncRef = useRef(false)
  const [alertPanel, setAlertPanel] = useState(null)
  const alerts = usePriceAlerts()
  const [alertDraft, setAlertDraft] = useState(null)
  const closeAlerts = () => {
    setAlertPanel(null)
    alerts?.select(null)
    setPaneFocusRequest({ slot: topLeftSlot })
  }
  const openAlerts = (price) => {
    if (Number.isFinite(price)) return setAlertDraft({ price, symbol: chartProps.symbol, key: Date.now() })
    if (!hydrated) return { ok: false, reason: 'layout_loading' }
    if (alertPanel) closeAlerts()
    else { setAlertPanel({ key: Date.now() }); setActive(topLeftSlot); setMaximized(null) }
    return { ok: true }
  }
  useEffect(() => { setAlertDraft(null) }, [chartProps.symbol])
  const [vwapTarget, setVwapTarget] = useState(null)
  const { layout, ratio, topRatio, bottomRatio, lower, lowerRight, upperLeft, syncCrosshair } = preferences
  const visibleSlots = LAYOUT_SLOTS[layout]
  const multiple = layout !== 'single'
  const isHidden = (slot) => !visibleSlots.includes(slot) || Boolean(maximized && maximized !== slot)
  const selectedSlot = visibleSlots.includes(active) ? active : 'top'
  const topLeftSlot = ['quad', 'tripleTop'].includes(layout) ? 'upperLeft' : 'top'
  const rsSlot = paneType === 'relative-strength' ? topLeftSlot : null
  const alertSlot = alertPanel ? topLeftSlot : null
  const selectedIsAlerts = selectedSlot === alertSlot
  const selectedIsRs = selectedSlot === rsSlot && !selectedIsAlerts
  const selectPaneType = (value) => {
    if (!hydrated) return { ok: false, reason: 'layout_loading' }
    setAlertPanel(null)
    alerts?.select(null)
    setPaneType(value)
    update({ paneType: value })
    setActive(topLeftSlot)
    setMaximized(null)
    return { ok: true }
  }
  const rsProfile = shared.profiles[preferences.rsTimeframe]?.relativeStrength
  const rsSettings = rsProfile ? normalize({ ...preferences, ...rsProfile }, toolbarProps) : preferences
  const editRs = patch => {
    if (shared.status === 'disabled') update(patch)
    else if (rsProfile) shared.store.edit(preferences.rsTimeframe, { relativeStrength: { ...rsProfile, ...patch } })
  }
  const sharedControls = (timeframe) => shared.profiles[timeframe]?.controls || {}
  const topTimeframe = shared.status === 'disabled' ? toolbarProps.timeframe : preferences.topTimeframe
  const topProfile = { ...toolbarProps, timeframe: topTimeframe, positionOverlayEnabled: preferences.topPositionOverlayEnabled ?? toolbarProps.positionOverlayEnabled, ...sharedControls(topTimeframe) }
  const selectedProfile = selectedSlot === 'top' ? topProfile : { ...preferences[selectedSlot], ...sharedControls(preferences[selectedSlot].timeframe) }
  const chartTypeForSlot = (slot) => sharedControls((slot === 'top' ? topProfile : preferences[slot]).timeframe).chartTypesByTheme?.[theme] ?? preferences.chartTypesByTheme[theme][slot]
    ?? (slot === 'top' ? toolbarProps : preferences[slot]).chartType

  useEffect(() => {
    let cancelled = false
    readServerChartPreference({ scope: 'position', preferenceKey, userId }).then(async (saved) => {
      const value = saved || await readServerChartPreference({ scope: 'position', preferenceKey: legacyPreferenceKey, userId })
      if (cancelled) return
      const next = normalize(value?.version === 1 ? value : null, toolbarProps)
      preferencesRef.current = next
      setPreferences(next)
      setPaneType(next.paneType)
      setActive(next.paneType === 'relative-strength' && ['quad', 'tripleTop'].includes(next.layout) ? 'upperLeft' : 'top')
      setMountedSlots(LAYOUT_SLOTS[next.layout])
      setHydrated(true)
    })
    return () => { cancelled = true }
    // The caller keys this component by user ID. Symbol changes retain layout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const save = useCallback((value) => {
    if (!userId) return
    const revision = ++saveRevision.current
    saveQueue.current = saveQueue.current.catch(() => {}).then(async () => {
      const ok = await writeServerChartPreference({ scope: 'position', preferenceKey, userId, preferences: value })
      if (revision === saveRevision.current) setSaveFailed(!ok)
    })
  }, [userId])
  const update = useCallback((patch, persist = true) => {
    const next = { ...preferencesRef.current, ...patch }
    preferencesRef.current = next
    setPreferences(next)
    if (persist) save(next)
  }, [save])
  const updateSelected = (patch) => update({ [selectedSlot]: { ...preferencesRef.current[selectedSlot], ...patch } })
  const editSharedControls = (patch) => {
    const tf=selectedProfile.timeframe
    return shared.store.edit(tf, { controls: { ...shared.profiles[tf]?.controls, ...patch } })
  }
  const selectChartType = (chartType) => {
    if (!hydrated || selectedIsRs || selectedIsAlerts || !CHART_TYPES.has(chartType)) return
    if (shared.status !== 'disabled') {
      void editSharedControls({ chartTypesByTheme: { ...shared.profiles[selectedProfile.timeframe]?.controls?.chartTypesByTheme, [theme]: chartType } })
      return
    }
    const otherTheme = theme === 'light' ? 'dark' : 'light'
    const saved = preferencesRef.current.chartTypesByTheme
    // Pin the legacy fallback before updating it, so the other theme stays unchanged.
    const chartTypesByTheme = {
      ...saved,
      [theme]: { ...saved[theme], [selectedSlot]: chartType },
      [otherTheme]: { ...saved[otherTheme], [selectedSlot]: saved[otherTheme][selectedSlot] ?? selectedProfile.chartType },
    }
    update({ chartTypesByTheme, ...(selectedSlot === 'top' ? {} : {
      [selectedSlot]: { ...preferencesRef.current[selectedSlot], chartType },
    }) })
    if (selectedSlot === 'top') toolbarProps.onChartTypeChange(chartType)
  }
  const selectTimeframe = (value) => {
    if (selectedIsAlerts) return
    if (!hydrated || !TIMEFRAMES.has(value)) return
    if (selectedIsRs) {
      update({ rsTimeframe: value, ...(shared.status === 'disabled' && !EXTENDED.has(value) ? { rsMarketSession: 'regular' } : {}) })
      return
    }
    if (selectedSlot !== 'top') updateSelected({ timeframe: value, ...(!EXTENDED.has(value) ? { marketSession: 'regular' } : {}) })
    else if (shared.status !== 'disabled') update({ topTimeframe: value })
    else toolbarProps.onTimeframeChange(value)
  }
  const changeLayout = (value) => {
    if (!Object.hasOwn(LAYOUT_SLOTS, value)) return
    setMaximized(null)
    setActive((alertPanel || paneType !== 'chart') && ['quad', 'tripleTop'].includes(value) ? 'upperLeft' : 'top')
    setMountedSlots(previous => SLOTS.filter(slot => previous.includes(slot) || LAYOUT_SLOTS[value].includes(slot)))
    update({ layout: value })
  }
  const clearCrosshairs = useCallback(() => {
    SLOTS.forEach(slot => chartRefs[slot].current?.setSyncedCrosshair?.(null))
    rsRef.current?.setSyncedCrosshair?.(null)
  }, [chartRefs])
  useEffect(() => {
    if (syncCrosshair || previousSyncRef.current) clearCrosshairs()
    previousSyncRef.current = syncCrosshair
  }, [clearCrosshairs, layout, syncCrosshair, maximized, rsSlot, alertSlot, preferences.rsTimeframe, rsSettings.rsMarketSession, chartProps.symbol, toolbarProps.timeframe, lower.timeframe, lowerRight.timeframe, upperLeft.timeframe])
  const crosshairHandlers = useMemo(() => Object.fromEntries(SLOTS.map(source => [source, point => {
    if (!syncCrosshair || maximized || !visibleSlots.includes(source)) return
    visibleSlots.forEach(slot => {
      if (slot !== source && slot !== alertSlot) (slot === rsSlot ? rsRef : chartRefs[slot]).current?.setSyncedCrosshair?.(point)
    })
  }])), [chartRefs, visibleSlots, syncCrosshair, maximized, rsSlot, alertSlot])
  const topOverlay = useMemo(() => getOverlayProps(toolbarProps.timeframe, topProfile.positionOverlayEnabled),
    [getOverlayProps, toolbarProps.timeframe, topProfile.positionOverlayEnabled])
  const lowerOverlay = useMemo(() => getOverlayProps(lower.timeframe, lower.positionOverlayEnabled),
    [getOverlayProps, lower.timeframe, lower.positionOverlayEnabled])
  const lowerRightOverlay = useMemo(() => getOverlayProps(lowerRight.timeframe, lowerRight.positionOverlayEnabled),
    [getOverlayProps, lowerRight.timeframe, lowerRight.positionOverlayEnabled])
  const upperLeftOverlay = useMemo(() => getOverlayProps(upperLeft.timeframe, upperLeft.positionOverlayEnabled),
    [getOverlayProps, upperLeft.timeframe, upperLeft.positionOverlayEnabled])
  const overlays = { top: topOverlay, lower: lowerOverlay, lowerRight: lowerRightOverlay, upperLeft: upperLeftOverlay }

  const maximize = (event, slot) => {
    if (drawingTool !== 'select' || !multiple || !event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    if (event.target.closest('button,input,select,textarea,[role="button"],[role="separator"]')) return
    event.preventDefault()
    event.stopPropagation()
    setActive(slot)
    setMaximized(value => value ? null : slot)
  }
  const activeToolbar = selectedSlot !== 'top' ? {
    ...toolbarProps, ...selectedProfile,
    onMarketSessionChange: (marketSession) => updateSelected({ marketSession }),
    extendedHoursEnabled: EXTENDED.has(selectedProfile.timeframe),
    onPositionOverlayToggle: () => updateSelected({ positionOverlayEnabled: !selectedProfile.positionOverlayEnabled }),
  } : { ...topProfile, onPositionOverlayToggle: () => { if (hydrated) update({ topPositionOverlayEnabled: !topProfile.positionOverlayEnabled }) } }
  if (shared.status !== 'disabled' && !selectedIsRs) {
    activeToolbar.onMarketSessionChange = marketSession => editSharedControls({ marketSession })
    activeToolbar.onPositionOverlayToggle = () => editSharedControls({ positionOverlayEnabled: !selectedProfile.positionOverlayEnabled })
  }
  if (selectedIsRs) {
    activeToolbar.timeframe = preferences.rsTimeframe
    activeToolbar.marketSession = rsSettings.rsMarketSession
    activeToolbar.extendedHoursEnabled = EXTENDED.has(preferences.rsTimeframe)
    activeToolbar.onMarketSessionChange = marketSession => editRs({ rsMarketSession: EXTENDED.has(preferences.rsTimeframe) && marketSession === 'all' ? 'all' : 'regular' })
    activeToolbar.onPositionOverlayToggle = undefined
    if (rsActiveRole === 'benchmark') {
      activeToolbar.symbol = rsSettings.rsBenchmark
      activeToolbar.onSymbolChange = value => { const next = value.trim().toUpperCase(); if (/^[A-Z0-9][A-Z0-9.!^-]{0,14}$/.test(next)) editRs({ rsBenchmark: next }) }
      activeToolbar.onSymbolSearchOpen = undefined
    }
  }
  const paneHandlers = Object.fromEntries(WORKSPACE_TOOLBAR_ITEMS.filter(item => item.section === 'panes').map(item => [item.id, () => item.id === 'position.alerts.open' ? openAlerts() : selectPaneType(item.paneType)]))
  const runDrawingAction = action => {
    if (!hydrated || selectedIsAlerts) return { ok: false, reason: 'chart_unavailable' }
    return (selectedIsRs ? rsRef : chartRefs[selectedSlot]).current?.drawingAction(action)
  }
  const drawingHandlers = Object.fromEntries(WORKSPACE_TOOLBAR_ITEMS.filter(item => item.section === 'drawings').map(item => [item.id, () => {
    if (!hydrated) return { ok: false, reason: 'layout_loading' }
    if (['undo', 'redo', 'delete', 'settings', 'selectAll', 'clearAll'].includes(item.tool)) return runDrawingAction(item.tool === 'delete' ? 'remove' : item.tool)
    else setDrawingTool(item.tool)
  }]))
  useCommandHandlers({
    ...paneHandlers, ...drawingHandlers,
    'position.chart.price_alert.create': () => {
      if (!hydrated) return { ok: false, reason: 'layout_loading' }
      for (const slot of visibleSlots) {
        if (slot === rsSlot || slot === alertSlot || (maximized && maximized !== slot)) continue
        if (chartRefs[slot].current?.createPriceAlertAtCrosshair?.()) return { ok: true }
      }
      return { ok: false, reason: 'Hover the price chart to choose an alert price' }
    },
    ...Object.fromEntries(CHART_PANE_COMMANDS.map(item => [item.id, () => {
      if (!hydrated) return { ok: false, reason: 'layout_loading' }
      const slot = PANE_ORDER[layout][item.pane - 1]
      if (!slot) return { ok: false, reason: 'pane_unavailable' }
      setActive(slot)
      setMaximized(item.action === 'expand' && multiple && maximized !== slot ? slot : null)
      // Focus after React reveals the target so keyboard controls follow the pane.
      setPaneFocusRequest({ slot })
    }])),
    ...Object.fromEntries(CHART_LAYOUT_COMMANDS.map(item => [item.id, () => {
      if (!hydrated) return { ok: false, reason: 'layout_loading' }
      changeLayout(item.layout)
    }])),
    'position.chart.session.regular': () => {
      if (!hydrated || selectedIsAlerts || !activeToolbar.onMarketSessionChange) return { ok: false, reason: 'session_unavailable' }
      activeToolbar.onMarketSessionChange('regular')
    },
    'position.chart.session.extended': () => {
      if (!hydrated || selectedIsAlerts || !activeToolbar.extendedHoursEnabled || !activeToolbar.onMarketSessionChange) return { ok: false, reason: 'extended_hours_unavailable' }
      activeToolbar.onMarketSessionChange('all')
    },
    'position.chart.candles': () => { if (!hydrated) return { ok: false, reason: 'layout_loading' }; selectChartType('candlestick') },
    'position.chart.bars': () => { if (!hydrated) return { ok: false, reason: 'layout_loading' }; selectChartType('bar') },
    'position.chart.position.toggle': () => { if (!hydrated || selectedIsAlerts || !activeToolbar.onPositionOverlayToggle) return { ok: false, reason: 'overlay_unavailable' }; activeToolbar.onPositionOverlayToggle() },
    'position.chart.gex.toggle': () => ({ ok: false, reason: 'gex_unavailable' }),
  })
  const invokePaneCommand = (id) => commandContext ? commandContext.invokeCommand(id) : ({ ...paneHandlers, ...drawingHandlers })[id]?.()
  const slotStyle = (slot) => {
    const upper = slot === 'top' || slot === 'upperLeft'
    const rowSplit = upper ? ['quad', 'tripleTop'].includes(layout) : ['triple', 'quad'].includes(layout)
    const left = upper ? slot === 'upperLeft' : slot === 'lower'
    const widthRatio = upper ? topRatio : bottomRatio
    const full = !multiple || Boolean(maximized)
    return {
      display: isHidden(slot) ? 'none' : 'flex',
      top: full || upper ? '0%' : `${ratio * 100}%`,
      left: full || !rowSplit || left ? '0%' : `${widthRatio * 100}%`,
      width: full || !rowSplit ? '100%' : `${(left ? widthRatio : 1 - widthRatio) * 100}%`,
      height: full ? '100%' : `${(upper ? ratio : 1 - ratio) * 100}%`,
    }
  }
  const separator = (key, label, vertical, style) => <div key={key} role="separator" aria-label={label}
    aria-orientation={vertical ? 'vertical' : 'horizontal'} aria-valuemin={20} aria-valuemax={80}
    aria-valuenow={Math.round(preferences[key] * 100)} tabIndex={0}
    className={`absolute z-30 touch-none outline-none ${vertical ? 'w-[9px] -translate-x-1/2 cursor-col-resize' : 'left-0 right-0 h-[9px] -translate-y-1/2 cursor-row-resize'}`}
    style={style}
    onPointerDown={(event) => {
      event.preventDefault()
      dragRef.current = { pointerId: event.pointerId, key, ratio: preferencesRef.current[key] }
      event.currentTarget.setPointerCapture?.(event.pointerId)
    }}
    onPointerMove={(event) => {
      if (dragRef.current?.pointerId !== event.pointerId || dragRef.current?.key !== key) return
      const rect = containerRef.current.getBoundingClientRect()
      const length = vertical ? rect.width : rect.height
      if (length > 0) update({ [key]: clampRatio((vertical ? event.clientX - rect.left : event.clientY - rect.top) / length) }, false)
    }}
    onPointerUp={(event) => {
      if (dragRef.current?.pointerId !== event.pointerId || dragRef.current?.key !== key) return
      dragRef.current = null
      event.currentTarget.releasePointerCapture?.(event.pointerId)
      save(preferencesRef.current)
    }}
    onPointerCancel={() => {
      if (dragRef.current?.key !== key) return
      update({ [key]: dragRef.current.ratio }, false)
      dragRef.current = null
    }}
    onKeyDown={(event) => {
      const backward = vertical ? 'ArrowLeft' : 'ArrowUp'
      const forward = vertical ? 'ArrowRight' : 'ArrowDown'
      if (![backward, forward, 'Home'].includes(event.key)) return
      event.preventDefault()
      event.stopPropagation()
      update({ [key]: event.key === 'Home' ? 0.5 : clampRatio(preferencesRef.current[key] + (event.key === backward ? -0.02 : 0.02)) })
    }}>
    <div className={`absolute bg-border ${vertical ? 'top-0 bottom-0 left-1/2 w-px' : 'left-0 right-0 top-1/2 h-px'}`} />
  </div>
  const labels = { top: ['quad', 'tripleTop'].includes(layout) ? 'Top right' : 'Top', lower: ['split', 'tripleTop'].includes(layout) ? 'Bottom' : 'Bottom left', lowerRight: 'Bottom right', upperLeft: 'Top left' }
  const layoutControl = <ChartLayoutMenu layout={layout} syncCrosshair={syncCrosshair} disabled={!hydrated}
    onLayoutChange={changeLayout} onSyncCrosshairChange={(value) => update({ syncCrosshair: value })} />
  const dailyRequested = (mountedSlots.some(slot => (slot === 'top' ? topProfile : preferences[slot]).timeframe === 'daily') || (rsSlot && preferences.rsTimeframe === 'daily'))
  return <SharedAnchoredVwap key={userId} userId={userId} symbol={chartProps.symbol} enabled={Boolean(chartProps.retainedVwapEnabled) && chartProps.assetType !== 'option'}>
    <WorkspaceDailySource chartProps={chartProps} enabled={Boolean(dailyRequested)}>
    <DailyPivotSourceProvider>
    <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
    <WorkspacePaneToolbar recentAlertCount={alerts?.recentUnreadCount || 0} paneType={alertPanel ? 'alerts' : paneType} drawingTool={drawingTool} onCommand={invokePaneCommand} onPenSettings={() => runDrawingAction('penSettings')} disabled={!hydrated} />
    <div className="flex flex-1 min-h-0 min-w-0 flex-col">
    {!selectedIsAlerts && <ChartTopBar {...activeToolbar} onTimeframeChange={selectTimeframe}
      chartType={chartTypeForSlot(selectedSlot)} onChartTypeChange={selectChartType} chartTypeDisabled={!hydrated || selectedIsRs}
      additionalTools={<div ref={setVwapTarget} data-testid="position-vwap-toolbar-slot" />}
      layoutControl={<div className="flex items-center gap-2">{layoutControl}<label className="flex items-center gap-1 text-[10px] text-inkMuted"><input type="checkbox" aria-label="Show alerts on selected chart" checked={preferences.alertsVisible[selectedSlot]} disabled={!hydrated || selectedIsRs} onChange={e=>update({alertsVisible:{...preferences.alertsVisible,[selectedSlot]:e.target.checked}})}/>Alerts</label></div>}
      onSettingsOpen={() => selectedIsRs ? rsRef.current?.openSettings?.() : chartRefs[selectedSlot].current?.openSettings?.()} />}
    {selectedIsAlerts && <div className="flex h-10 shrink-0 items-center justify-between px-2 py-1 border-b border-border bg-canvas"><span className="text-xs text-inkMuted">{chartProps.symbol} · Alerts</span>{layoutControl}</div>}
    {saveFailed && <button type="button" className="text-xs text-inkMuted" onClick={() => save(preferencesRef.current)}>
      Chart layout could not save. Retry
    </button>}
    <ChartIntervalCommandSurface className="relative flex-1 min-h-0 overflow-hidden"
      disabled={selectedIsAlerts || drawingTool !== 'select'} onTimeframeChange={selectTimeframe}>
      <div ref={containerRef} data-testid="workspace-chart-stack" className="relative h-full min-h-0">
        {mountedSlots.map((slot) => {
          const isTop = slot === 'top'
          const hidden = isHidden(slot)
          const isRs = slot === rsSlot
          const isAlerts = slot === alertSlot
          const alternate = isRs || isAlerts
          const profile = isTop ? topProfile : { ...preferences[slot], ...sharedControls(preferences[slot].timeframe) }
          return <div key={slot} ref={element => { paneElements.current[slot] = element }} tabIndex={-1} data-testid={`workspace-chart-${slot}`} data-active={active === slot}
            aria-label={`${labels[slot]} ${isAlerts ? 'alerts' : isRs ? 'relative strength' : 'chart'}`} inert={hidden ? '' : undefined}
            className="absolute min-h-0 min-w-0 flex-col outline-none" style={slotStyle(slot)}
            onPointerDownCapture={() => setActive(slot)} onFocusCapture={() => setActive(slot)}
            onDoubleClickCapture={(event) => maximize(event, slot)}>
            <div className="absolute inset-0 flex min-h-0 flex-col" style={{ visibility: alternate ? 'hidden' : 'visible' }}
              inert={alternate ? '' : undefined} aria-hidden={alternate || undefined}>
            <TradeChart {...chartProps} {...(shared.status === 'disabled' ? overlays[slot] : getOverlayProps(profile.timeframe, profile.positionOverlayEnabled))} preferenceUserId={userId}
              priceAlertsVisible={preferences.alertsVisible[slot]} onPriceAlertCreate={openAlerts}
              drawingTool={drawingTool} onDrawingToolChange={setDrawingTool}
              ref={chartRefs[slot]} timeframe={profile.timeframe} chartType={chartTypeForSlot(slot)}
              marketSession={profile.marketSession} storageScope={isTop ? 'position' : `position-${slot}`}
              fallbackStorageScope={isTop ? null : 'position'}
              keyboardActive={!hidden && !alternate && active === slot} crosshairEnabled
              onCrosshairMove={alternate ? undefined : crosshairHandlers[slot]}
              retainedVwapToolbarTarget={active === slot && !hidden && !isAlerts ? vwapTarget : null} />
            </div>
            {isRs && <div className="absolute inset-0 flex min-h-0" style={{ visibility: isAlerts ? 'hidden' : 'visible' }} inert={isAlerts ? '' : undefined} aria-hidden={isAlerts || undefined}><RelativeStrengthCharts ref={rsRef} userId={userId} chartProps={chartProps}
              onCrosshairMove={crosshairHandlers[slot]} benchmark={rsSettings.rsBenchmark} activeRole={rsActiveRole} onActiveRoleChange={setRsActiveRole}
              marginsByTimeframe={rsSettings.rsMargins} onMarginsChange={rsMargins => editRs({ rsMargins })}
              colors={rsSettings.rsColors}
              appearance={rsSettings.rsAppearance} onAppearanceChange={rsAppearance => editRs({ rsAppearance })}
              timeframe={preferences.rsTimeframe} marketSession={rsSettings.rsMarketSession} tool={drawingTool}
              onToolChange={setDrawingTool} keyboardActive={!hidden && !isAlerts && active === slot} /></div>}
            {isAlerts && <div className="absolute inset-0 z-10 min-h-0"><PriceAlertPanel symbol={chartProps.symbol} requestKey={alertPanel.key} onClose={closeAlerts} onSymbolChange={onOpenSymbol} /></div>}
            {multiple && <div aria-hidden className="pointer-events-none absolute inset-0 z-20"
              style={{ boxShadow: active === slot ? 'inset 0 0 0 1px rgba(128,128,128,0.22)' : 'none' }} />}
          </div>
        })}
        {multiple && !maximized && <>
          {separator('ratio', 'Resize chart heights', false, { top: `${ratio * 100}%` })}
          {['quad', 'tripleTop'].includes(layout) && separator('topRatio', 'Resize top chart widths', true, { top: 0, height: `${ratio * 100}%`, left: `${topRatio * 100}%` })}
          {['triple', 'quad'].includes(layout) && separator('bottomRatio', 'Resize bottom chart widths', true, { top: `${ratio * 100}%`, bottom: 0, left: `${bottomRatio * 100}%` })}
        </>}
      </div>
    </ChartIntervalCommandSurface>
    </div>
    {alertDraft && <PriceAlertDialog key={alertDraft.key} symbol={alertDraft.symbol} initialPrice={alertDraft.price} onClose={()=>setAlertDraft(null)} />}
    </div>
    </DailyPivotSourceProvider>
    </WorkspaceDailySource>
  </SharedAnchoredVwap>
}
